import {
    Box,
    Divider,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ChevronRight as ChevronRightIcon,
    Error as ErrorIcon,
    Verified as VerifiedIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import {ValidationResult} from '../../types/pending_docs';

export interface ValidationPanelProps {
    /** Validation result data */
    validation: ValidationResult;
    /** Whether the panel is expanded */
    expanded: boolean;
    /** Toggle expanded state */
    onToggleExpanded: () => void;
    /** Currently selected chapter index (for highlighting) */
    selectedChapterIndex?: number;
    /** Called when a chapter is clicked in the validation list */
    onChapterClick?: (chapterIndex: number) => void;
}

/**
 * Get MUI color name based on score.
 */
function getScoreColor(score: number): 'success' | 'warning' | 'error' {
    if (score >= 80) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
}

/**
 * ValidationPanel - Collapsible sidebar displaying validation results.
 *
 * Shows overall score, document-level errors/warnings, and per-chapter validation scores.
 */
export function ValidationPanel({
    validation,
    expanded,
    onToggleExpanded,
    selectedChapterIndex,
    onChapterClick,
}: ValidationPanelProps) {
    // Determine severity based on overall score and errors
    const hasChapterErrors = validation.chapters.some(ch => ch.errors.length > 0);
    const hasChapterWarnings = validation.chapters.some(ch => ch.warnings.length > 0);
    const hasDocErrors = validation.errors.length > 0;
    const hasBrokenRefs = validation.broken_image_refs.length > 0;
    const hasAnyIssues = hasDocErrors || hasChapterErrors || hasBrokenRefs || hasChapterWarnings;

    // Count total issues
    const totalErrors = validation.chapters.reduce((sum, ch) => sum + ch.errors.length, 0) + validation.errors.length;
    const totalWarnings = validation.chapters.reduce((sum, ch) => sum + ch.warnings.length, 0) + validation.warnings.length + validation.broken_image_refs.length;

    const scoreColorName = getScoreColor(validation.overall_score);

    return (
        <Paper sx={{
            width: expanded ? 320 : 48,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'width',
            overflow: 'hidden',
            // The tinted header + score badge below already carry the status
            // color — the 3px side stripe this used to add was the exact
            // accent pattern the design system bans (side-stripe borders).
        }}>
            {/* Header */}
            <Box sx={{
                p: expanded ? 2 : 1,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: expanded ? 'space-between' : 'center',
                alignItems: 'center',
                bgcolor: hasAnyIssues
                    ? (totalErrors > 0 ? 'error.light' : 'warning.light')
                    : 'success.light',
            }}>
                {expanded ? (
                    <>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <IconButton
                                size="small"
                                onClick={onToggleExpanded}
                            >
                                <ChevronRightIcon fontSize="small"/>
                            </IconButton>
                            <Box>
                                <Typography variant="subtitle2" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    VALIDATION
                                    <Box
                                        component="span"
                                        sx={{
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1,
                                            fontSize: '0.875rem',
                                            fontWeight: 'bold',
                                            bgcolor: `${scoreColorName}.main`,
                                            color: `${scoreColorName}.contrastText`,
                                        }}
                                    >
                                        {validation.overall_score}%
                                    </Box>
                                </Typography>
                                {hasAnyIssues && (
                                    <Typography variant="caption" sx={{
                                        color: "text.secondary"
                                    }}>
                                        {totalErrors > 0 && `${totalErrors} error${totalErrors !== 1 ? 's' : ''}`}
                                        {totalErrors > 0 && totalWarnings > 0 && ', '}
                                        {totalWarnings > 0 && `${totalWarnings} warning${totalWarnings !== 1 ? 's' : ''}`}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Tooltip title={`Validation: ${validation.overall_score}% (${totalErrors} errors, ${totalWarnings} warnings)`}>
                        <IconButton
                            size="small"
                            onClick={onToggleExpanded}
                        >
                            {totalErrors > 0 ? (
                                <ErrorIcon fontSize="small" color="error"/>
                            ) : totalWarnings > 0 ? (
                                <WarningIcon fontSize="small" color="warning"/>
                            ) : (
                                <VerifiedIcon fontSize="small" color="success"/>
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {/* Content */}
            {expanded && (
                <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
                    {/* Summary */}
                    <Typography variant="body2" sx={{mb: 2}}>
                        {validation.is_valid
                            ? 'Document is valid and ready for ingestion'
                            : hasDocErrors
                                ? `Found ${validation.errors.length} document error(s)`
                                : hasChapterErrors
                                    ? 'Found chapters with validation errors'
                                    : `Found ${validation.broken_image_refs.length} broken image reference(s)`
                        }
                    </Typography>

                    {/* Document-level errors */}
                    {validation.errors.length > 0 && (
                        <Box sx={{mb: 2}}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: "bold",
                                    color: "error.main",
                                    display: 'block',
                                    mb: 0.5
                                }}>
                                Document Errors:
                            </Typography>
                            {validation.errors.map((err, i) => (
                                <Typography
                                    key={i}
                                    variant="caption"
                                    sx={{
                                        color: "error.main",
                                        display: 'block',
                                        pl: 1
                                    }}>
                                    • {err}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {/* Document-level warnings */}
                    {validation.warnings.length > 0 && (
                        <Box sx={{mb: 2}}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: "bold",
                                    color: "warning.main",
                                    display: 'block',
                                    mb: 0.5
                                }}>
                                Document Warnings:
                            </Typography>
                            {validation.warnings.map((warn, i) => (
                                <Typography
                                    key={i}
                                    variant="caption"
                                    sx={{
                                        color: "warning.main",
                                        display: 'block',
                                        pl: 1
                                    }}>
                                    • {warn}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Divider sx={{my: 2}}/>

                    {/* Per-chapter results */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: "bold",
                            color: "text.secondary",
                            display: 'block',
                            mb: 1
                        }}>
                        Chapter Scores:
                    </Typography>
                    {validation.chapters.map((ch) => {
                        const chapterHasErrors = ch.errors.length > 0;
                        const chapterHasWarnings = ch.warnings.length > 0;
                        const chapterHasBrokenRefs = (ch.broken_image_refs?.length || 0) > 0;
                        const chapterHasIssues = chapterHasErrors || chapterHasWarnings || chapterHasBrokenRefs;
                        const isSelected = ch.chapter_index === selectedChapterIndex;

                        return (
                            <Box
                                key={ch.chapter_index}
                                sx={{
                                    mb: 1.5,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                                    cursor: onChapterClick ? 'pointer' : 'default',
                                    '&:hover': onChapterClick ? {bgcolor: 'action.hover'} : {},
                                    borderLeft: chapterHasIssues ? 3 : 0,
                                    borderColor: chapterHasErrors ? 'error.main' : 'warning.main',
                                }}
                                onClick={() => onChapterClick?.(ch.chapter_index)}
                            >
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: chapterHasIssues ? 0.5 : 0}}>
                                    <Box
                                        sx={{
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            bgcolor: chapterHasErrors ? 'error.light' : ch.quality_score >= 80 ? 'success.light' : 'warning.light',
                                            color: chapterHasErrors ? 'error.contrastText' : ch.quality_score >= 80 ? 'success.contrastText' : 'warning.contrastText',
                                        }}
                                    >
                                        {ch.quality_score}%
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{
                                            fontWeight: isSelected ? 'bold' : 'normal',
                                            flex: 1
                                        }}>
                                        {ch.title || `Chapter ${ch.chapter_index + 1}`}
                                    </Typography>
                                </Box>
                                {/* Errors */}
                                {ch.errors.map((err, i) => (
                                    <Typography
                                        key={`err-${i}`}
                                        variant="caption"
                                        sx={{
                                            color: "error.main",
                                            display: 'block',
                                            pl: 1
                                        }}>
                                        • {err}
                                    </Typography>
                                ))}
                                {/* Warnings */}
                                {ch.warnings.map((warn, i) => (
                                    <Typography
                                        key={`warn-${i}`}
                                        variant="caption"
                                        sx={{
                                            color: "warning.main",
                                            display: 'block',
                                            pl: 1
                                        }}>
                                        • {warn}
                                    </Typography>
                                ))}
                                {/* Broken image refs */}
                                {ch.broken_image_refs?.map((ref, i) => (
                                    <Typography
                                        key={`ref-${i}`}
                                        variant="caption"
                                        sx={{
                                            color: "warning.main",
                                            display: 'block',
                                            pl: 1
                                        }}>
                                        • Broken image: {ref}
                                    </Typography>
                                ))}
                            </Box>
                        );
                    })}
                </Box>
            )}
            {/* Collapsed state - vertical text */}
            {!expanded && (
                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2}}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            writingMode: 'vertical-rl',
                            textOrientation: 'mixed'
                        }}>
                        {validation.overall_score}% - {totalErrors} err, {totalWarnings} warn
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}
